import type {
  MappingPresetPropertyRecord,
  MappingPresetRecord,
} from "src/services/mappingPresetService";
import type { IncomingValuesResponse } from "src/services/mappingService";
import type { MappingFormPrimitive, MappingRecord } from "./mapping.types";

export const getPresetName = (preset?: MappingPresetRecord | null) =>
  preset?.setName || preset?.name || "--";

export const getPresetRecords = (payload?: unknown): MappingPresetRecord[] => {
  const response = (payload as { data?: { response?: unknown } } | undefined)?.data?.response;
  const record =
    response && typeof response === "object" ? (response as Record<string, unknown>) : {};

  if (Array.isArray(response)) return response as MappingPresetRecord[];
  if (Array.isArray(record.presets)) return record.presets as MappingPresetRecord[];
  if (Array.isArray(record.resources)) return record.resources as MappingPresetRecord[];

  return [];
};

export const getPresetTotal = (payload: unknown, fallback: number) => {
  const response = (payload as { data?: { response?: Record<string, unknown> } } | undefined)?.data
    ?.response;

  return Number(response?.totalItems ?? response?.totalElements ?? response?.total ?? fallback);
};

export const getPresetPropertyLastValue = (
  property: MappingPresetPropertyRecord
): MappingFormPrimitive => {
  const value = property.lastValue ?? property.currentValue ?? property.value;

  return typeof value === "string" || typeof value === "number" || typeof value === "boolean"
    ? value
    : 0;
};

export const toPresetIncomingValues = (
  properties: MappingPresetPropertyRecord[],
  groupName: string
): IncomingValuesResponse => ({
  [groupName]: properties.reduce<IncomingValuesResponse[string]>((acc, property) => {
    const propertyName = property.propertyName || String(property.packetCode ?? "");
    if (!propertyName) return acc;

    acc[propertyName] = {
      packet_code: property.packetCode,
      data: [
        {
          value: getPresetPropertyLastValue(property),
        },
      ],
    };
    return acc;
  }, {}),
});

export const getUniquePresetProperties = (
  properties: MappingPresetPropertyRecord[],
  mappings: MappingRecord[]
) => {
  const seen = new Set<string>();

  const source = properties.length
    ? properties
    : mappings.map((mapping) => ({
        packetCode: String(mapping.packetCode ?? ""),
        propertyName: String(mapping.packetCode ?? ""),
      }));

  return source.reduce<MappingPresetPropertyRecord[]>((records, property) => {
    const packetCode = String(property.packetCode ?? "");
    if (!packetCode || seen.has(packetCode)) return records;

    seen.add(packetCode);

    records.push({
      ...property,
      packetCode,
      propertyName: property.propertyName || packetCode,
    });

    return records;
  }, []);
};
